import React, { useState, useEffect, Suspense } from 'react';
import SearchBar from './components/SearchBar';
import ConceptExplanation from './components/ConceptExplanation';
import ErrorBoundary from './components/ErrorBoundary';
import geminiService from './services/geminiService';
import { AlertCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import './App.css';

const Flashcards = React.lazy(() => import('./components/Flashcards'));
const Quiz = React.lazy(() => import('./components/Quiz'));

function App() {
  const [loading, setLoading] = useState(false);
  const [content, setContent] = useState(null);
  const [error, setError] = useState(null);
  const [currentConcept, setCurrentConcept] = useState('');

  const [stats, setStats] = useState({
    streak: 0,
    gems: 0,
    hearts: 5,
    completedTopics: []
  });

  useEffect(() => {
    const savedStats = localStorage.getItem('duolearn_stats');
    if (savedStats) {
      const parsedStats = JSON.parse(savedStats);
      if (!parsedStats.completedTopics) {
        parsedStats.completedTopics = [];
      }
      setStats(parsedStats);
    }
  }, []);

  const updateStats = (newStats) => {
    const updated = { ...stats, ...newStats };
    setStats(updated);
    localStorage.setItem('duolearn_stats', JSON.stringify(updated));
  };

  const handleSearch = async (concept) => {
    setLoading(true);
    setError(null);
    setContent(null);
    setCurrentConcept(concept);

    try {
      const learningContent = await geminiService.generateLearningContent(concept);
      setContent(learningContent);
      // Increment streak on first lesson of the day (simplified logic: just add 1 for demo)
      updateStats({ streak: stats.streak === 0 ? 1 : stats.streak });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const trendingTopics = [
    '🌱 Photosynthesis', '🌍 Gravity', '🗳️ Democracy', '🧬 DNA',
    '🌌 Black Holes', '💰 Cryptocurrency', '🤖 Machine Learning', '🎨 Renaissance'
  ];

  return (
    <div className="container">
      <SearchBar onSearch={handleSearch} loading={loading} stats={stats} />
      
      <ErrorBoundary>
        {loading && (
          <div className="card duo-lesson-card loading-card" aria-live="polite">
            <div className="mascot-container">
              <div className="loading-mascot" aria-hidden="true">
                🦉
              </div>
            </div>
            <div className="loading-message">
              🎯 Creating your personalized lesson...
            </div>
            <div className="progress-bar-container">
              <motion.div 
                className="duo-progress-bar"
                initial={{ overflow: 'hidden' }}
              >
                <motion.div 
                  className="duo-progress-fill"
                  initial={{ width: "0%" }}
                  animate={{ width: ["0%", "50%", "100%"] }}
                  transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                />
              </motion.div>
            </div>
            <p className="loading-subtitle">
              Hang tight! This will be worth it! ✨
            </p>
          </div>
        )}

        {error && (
          <div className="card duo-error error-card" aria-live="assertive">
            <div className="error-emoji" aria-hidden="true">😅</div>
            <div className="error-header">
              <AlertCircle size={28} style={{ color: '#ff4b4b' }} aria-hidden="true" />
              <h3 className="error-title">Oops! Something went wrong</h3>
            </div>
            <p className="error-message">{error}</p>
            <p className="error-subtitle">Don't worry, try again! We're here to help you learn! 💪</p>
          </div>
        )}

        {content && (
          <>
            <div className="card duo-success success-card">
              <div className="success-emoji" aria-hidden="true">🎉</div>
              <h2 className="success-title">
                Mastering: {currentConcept}
              </h2>
              <p className="success-subtitle">
                Let's dive into your personalized lesson! 🚀
              </p>
            </div>

            <ConceptExplanation 
              explanation={content.explanation} 
              keyPoints={content.keyPoints} 
            />
            
            <Suspense fallback={<div className="loading-message" style={{textAlign: 'center', margin: '20px'}}>Loading...</div>}>
              {content.flashcards && content.flashcards.length > 0 && (
                <Flashcards flashcards={content.flashcards} />
              )}
              
              {content.quiz && content.quiz.length > 0 && (
                <Quiz quiz={content.quiz} stats={stats} updateStats={updateStats} currentConcept={currentConcept} />
              )}
            </Suspense>
          </>
        )}

        {!loading && !content && !error && (
          <div className="card duo-lesson-card default-card">
            <div className="default-mascot" aria-hidden="true">🦉</div>
            <h3 className="default-title">
              Ready for Your Next Lesson?
            </h3>
            <p className="default-subtitle">
              Choose any topic and start your personalized learning journey! 🎯
            </p>
            
            <div className="trending-container">
              <h4 className="trending-title">
                🌟 Trending Topics
              </h4>
              <div className="trending-grid">
                {trendingTopics.map((topic, index) => {
                  const topicName = topic.split(' ').slice(1).join(' '); // Extract name without emoji
                  return (
                    <button 
                      key={index} 
                      className="trending-topic"
                      onClick={() => handleSearch(topicName)}
                      aria-label={`Learn about ${topicName}`}
                    >
                      {topic}
                    </button>
                  );
                })}
              </div>
            </div>

            {stats.completedTopics && stats.completedTopics.length > 0 && (
              <div className="trending-container" style={{ marginTop: '32px' }}>
                <h4 className="trending-title" style={{ color: '#58cc02' }}>
                  📚 Your Mastered Topics
                </h4>
                <div className="trending-grid">
                  {stats.completedTopics.map((topic, index) => (
                    <button 
                      key={index} 
                      className="trending-topic"
                      onClick={() => handleSearch(topic)}
                      aria-label={`Review ${topic}`}
                      style={{ 
                        border: '2px solid #58cc02', 
                        background: '#f0fdf4',
                        color: '#166534'
                      }}
                    >
                      🏆 {topic}
                    </button>
                  ))}
                </div>
              </div>
            )}
            
            <p className="footer-text">
              Start typing above to begin your learning adventure! ✨
            </p>
          </div>
        )}
      </ErrorBoundary>
    </div>
  );
}

export default App;